import { addDays, format, parseISO } from 'date-fns';
import { enUS, pl } from 'date-fns/locale';

/**
 * Locale-aware date-label formatters for period-bucketed time-series charts
 * (Citation Trends on the Sources page, Brand Score & Average Position on the
 * project preview page, …).
 *
 * Charts keep RAW keys in the first column of their matrix (so sorting and event
 * mark positioning via parseISO keep working). These formatters turn that raw
 * key into a human-readable label at the presentation layer:
 *
 * - Axis labels stay compact ("smart compact"): the month is shown once when the
 *   week stays within one month, both months when it crosses one, and the year is
 *   only shown when the bucket itself spans two years (the Dec→Jan week). All other
 *   year context lives in the tooltip + the filter date picker.
 * - Tooltip labels always carry the full date incl. the year.
 *
 * Month names follow the active UI language (Polish vs English) via date-fns
 * locales. Polish uses day-month ordering ("25–31 maj"); English month-day
 * ("May 25–31"). Polish month-only labels use the standalone form (LLLL → "maj"
 * nominative) while labels that include a day use the formatting form (MMMM →
 * "maja" genitive).
 *
 * Supported raw key shapes (covers both producers):
 * - `YYYY-MM-DD`               → single day (DAILY) or, with period=WEEKLY, a Monday-keyed week (end = +6 days)
 * - `YYYY-MM-DD - YYYY-MM-DD`  → an explicit ISO range (weekly buckets on the preview page)
 * - `YYYY-MM`                  → a month (MONTHLY); end = last day of month
 */

type BucketBounds = {
  start: Date;
  end: Date;
  /** true for WEEKLY/MONTHLY (a span), false for a single DAILY point */
  isRange: boolean;
  isMonthly: boolean;
};

const localeFor = (language: string | null | undefined) =>
  language?.startsWith('pl') ? pl : enUS;

const isPolish = (language: string | null | undefined): boolean => !!language?.startsWith('pl');

const parseBucket = (rawLabel: string, period?: string | null): BucketBounds | null => {
  const label = typeof rawLabel === 'string' ? rawLabel.trim() : '';
  if (!label) return null;

  // Explicit ISO range "YYYY-MM-DD - YYYY-MM-DD" (e.g. weekly buckets on the preview page)
  if (label.includes(' - ')) {
    const [startStr, endStr] = label.split(' - ');
    const start = parseISO(startStr.trim());
    const end = parseISO(endStr.trim());
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
    return { start, end, isRange: true, isMonthly: false };
  }

  // Monthly bucket key is "YYYY-MM"
  if (period === 'MONTHLY' || (!period && label.length === 7)) {
    const monthKey = label.length === 7 ? label : label.slice(0, 7);
    const start = parseISO(`${monthKey}-01`);
    if (isNaN(start.getTime())) return null;
    const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
    return { start, end, isRange: true, isMonthly: true };
  }

  const start = parseISO(label);
  if (isNaN(start.getTime())) return null;

  // Weekly bucket key is the Monday; the bucket spans 7 days (Mon–Sun)
  if (period === 'WEEKLY') {
    return { start, end: addDays(start, 6), isRange: true, isMonthly: false };
  }

  // DAILY or unknown single date
  return { start, end: start, isRange: false, isMonthly: false };
};

export type BucketLabelFormatters = {
  /** Compact label for the X axis */
  axis: (rawLabel: string) => string;
  /** Full label (with year) for the tooltip */
  tooltip: (rawLabel: string) => string;
};

/**
 * Builds axis + tooltip formatters bound to a bucket period and UI language.
 * Pure: pass `i18n.language` in so the result is memoizable and testable.
 */
export const buildBucketLabelFormatters = (
  period: string | null | undefined,
  language: string | null | undefined
): BucketLabelFormatters => {
  const locale = localeFor(language);
  const polish = isPolish(language);

  const axis = (rawLabel: string): string => {
    const bucket = parseBucket(rawLabel, period);
    if (!bucket) return rawLabel;

    const { start, end, isRange, isMonthly } = bucket;

    if (isMonthly) {
      return format(start, 'LLL yyyy', { locale });
    }

    if (!isRange) {
      return polish ? format(start, 'd MMM', { locale }) : format(start, 'MMM d', { locale });
    }

    const sameYear = start.getFullYear() === end.getFullYear();
    const sameMonth = sameYear && start.getMonth() === end.getMonth();

    if (polish) {
      if (sameMonth) return `${format(start, 'd', { locale })}–${format(end, 'd MMM', { locale })}`;
      if (sameYear)
        return `${format(start, 'd MMM', { locale })} – ${format(end, 'd MMM', { locale })}`;
      return `${format(start, "d MMM ''yy", { locale })} – ${format(end, "d MMM ''yy", { locale })}`;
    }

    if (sameMonth) return `${format(start, 'MMM d', { locale })}–${format(end, 'd', { locale })}`;
    if (sameYear)
      return `${format(start, 'MMM d', { locale })} – ${format(end, 'MMM d', { locale })}`;
    return `${format(start, "MMM d ''yy", { locale })} – ${format(end, "MMM d ''yy", { locale })}`;
  };

  const tooltip = (rawLabel: string): string => {
    const bucket = parseBucket(rawLabel, period);
    if (!bucket) return rawLabel;

    const { start, end, isRange, isMonthly } = bucket;

    if (isMonthly) {
      return format(start, 'LLLL yyyy', { locale });
    }

    if (!isRange) {
      return polish
        ? format(start, 'd MMMM yyyy', { locale })
        : format(start, 'MMMM d, yyyy', { locale });
    }

    const sameYear = start.getFullYear() === end.getFullYear();
    const sameMonth = sameYear && start.getMonth() === end.getMonth();

    if (polish) {
      if (sameMonth)
        return `${format(start, 'd', { locale })}–${format(end, 'd MMMM yyyy', { locale })}`;
      if (sameYear)
        return `${format(start, 'd MMMM', { locale })} – ${format(end, 'd MMMM yyyy', { locale })}`;
      return `${format(start, 'd MMMM yyyy', { locale })} – ${format(end, 'd MMMM yyyy', { locale })}`;
    }

    if (sameMonth)
      return `${format(start, 'MMMM d', { locale })} – ${format(end, 'd, yyyy', { locale })}`;
    if (sameYear)
      return `${format(start, 'MMMM d', { locale })} – ${format(end, 'MMMM d, yyyy', { locale })}`;
    return `${format(start, 'MMMM d, yyyy', { locale })} – ${format(end, 'MMMM d, yyyy', { locale })}`;
  };

  return { axis, tooltip };
};